import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useToast } from 'components/ui/toast';
import gql from 'graphql-tag';
import {
	Mutation,
	Notification,
	NotificationState,
	Query,
	QueryNotificationsArgs,
} from 'middleware-types';
import { isDemo } from './utils';
import { client } from './apollo';
import { EMBLEM_FIELDS } from 'components/ui/emblem/fragments';
import { useState } from 'react';

const PAGE_SIZE = 50;

/**
 * The Notifications query.
 */
export const NOTIFICATIONS = gql`
	${EMBLEM_FIELDS}
	query notifications($limit: Int!, $offset: Int!, $markAllAsSeen: Boolean!) {
		notifications(limit: $limit, offset: $offset, markAllAsSeen: $markAllAsSeen) {
			items {
				id
				recipientUserId
				displayText
				createdUtc
				relatedEntityId
				relatedEntityType
				type
				state
				targetEntityId
				relatedEntityEmblem {
					...EmblemFields
				}
			}
			totalCount
			unseenCount
		}
	}
`;

/**
 * The mutation to mark a notification as read by notification id.
 */
export const NOTIFICATION_MARK_READ = gql`
	mutation notificationMarkRead($notificationId: ID!) {
		notificationMarkRead(notificationId: $notificationId)
	}
`;

/**
 * The mutation to mark all notifications as read.
 */
export const NOTIFICATION_MARK_ALL_READ = gql`
	mutation notificationMarkAllRead($types: [NotificationType!]) {
		notificationMarkAllRead(types: $types)
	}
`;

/**
 * The mutation to dismiss a notification by notification id.
 */
export const NOTIFICATION_DISMISS = gql`
	mutation notificationDismiss($notificationId: ID!) {
		notificationDismiss(notificationId: $notificationId)
	}
`;

/**
 * The mutation to dismiss all notifications.
 */
export const NOTIFICATION_DISMISS_ALL = gql`
	mutation notificationDismissAll($types: [NotificationType!]) {
		notificationDismissAll(types: $types)
	}
`;

/**
 * Hook to get the user's notifications.
 * @param pageSize
 * @param offset
 * @param markAllAsSeen
 * @returns
 */
export const useNotificationsQuery = () => {
	const toast = useToast();
	const { data, error, loading, fetchMore } = useQuery<
		Pick<Query, 'notifications'>,
		QueryNotificationsArgs
	>(NOTIFICATIONS, {
		variables: {
			offset: 0,
			limit: PAGE_SIZE,
			markAllAsSeen: true,
		},
		onError: () => {
			toast.push(`An error occurred. Please try again later or contact Support.`, {
				variant: 'error',
			});
			console.log(error);
		},
		fetchPolicy: 'network-only',
	});

	const notifications = data?.notifications.items ?? [];
	const totalCount = data?.notifications.totalCount ?? 0;
	const unseenCount = data?.notifications.unseenCount ?? 0;

	const [isRefetching, setIsRefetching] = useState(false);
	const loadMore = async () => {
		if (loading) return;
		if (isRefetching) return;
		setIsRefetching(true);
		await fetchMore({
			variables: {
				offset: notifications.length,
				limit: PAGE_SIZE,
				markAllAsSeen: true,
			},
		});
		setIsRefetching(false);
	};

	return {
		notifications,
		totalCount,
		unseenCount,
		loading,
		loadMore,
	};
};

/**
 * The hook to dismiss all notifications.
 * @returns
 */
export const useNotificationDismissAllMutation = () => {
	const toast = useToast();
	const [notificationDismissAll, { error }] = useMutation<
		Pick<Mutation, 'notificationDismissAll'>
	>(NOTIFICATION_DISMISS_ALL, {
		errorPolicy: 'all',
		refetchQueries: [
			{
				query: NOTIFICATIONS,
				variables: {
					limit: 0,
					offset: 0,
					markAllAsSeen: false,
				},
			},
		],
		awaitRefetchQueries: true,
		onError: () => {
			toast.push(`An error occurred. Please try again later or contact Support.`, {
				variant: 'error',
			});
			console.log(error);
		},
	});

	const dismissAllNotifications = async () => {
		return notificationDismissAll();
	};

	return { dismissAllNotifications };
};

/**
 * The hook to mark all notifications as read.
 * @returns
 */
export const useNotificationMarkAllReadMutation = () => {
	const client = useApolloClient();
	const toast = useToast();
	const [notificationMarkAllRead, { error }] = useMutation<
		Pick<Mutation, 'notificationMarkAllRead'>
	>(NOTIFICATION_MARK_ALL_READ, {
		errorPolicy: 'all',
		refetchQueries: [
			{
				query: NOTIFICATIONS,
				variables: {
					limit: 0,
					offset: 0,
					markAllAsSeen: false,
				},
			},
		],
		update: (cache, data) => {
			const cacheData: Notification[] = client.readQuery({
				query: NOTIFICATIONS,
			})?.notifications?.notifications?.items;
			for (let i = 0; i < cacheData?.length; i++) {
				cache.writeFragment({
					id: `Notification:${cacheData[i].id}`,
					fragment: gql`
						fragment newState on Notification {
							state
						}
					`,
					data: {
						state: NotificationState.Read,
					},
				});
			}
			return data;
		},
		onError: () => {
			toast.push(`An error occurred. Please try again later or contact Support.`, {
				variant: 'error',
			});
			console.log(error);
		},
	});

	const readAllNotifications = async () => {
		return notificationMarkAllRead();
	};

	return { readAllNotifications };
};

// get the badge counts for notifications, connections etc.
const MARK_NOTIFICATIONS_SEEN = gql`
	query MarkNotificationsSeen($targetEntityId: String) {
		notifications(
			limit: 0
			offset: 0
			markAllAsSeen: true
			targetEntityId: $targetEntityId
			types: [ConnectionRequested]
		) {
			unseenCount
		}
	}
`;

export const markNotificationsSeen = (targetEntityId: string) => {
	const { data } = useQuery<Pick<Query, 'notifications'>>(MARK_NOTIFICATIONS_SEEN, {
		fetchPolicy: 'cache-and-network',
		pollInterval: !isDemo() ? 20000 : undefined,
		skip: !isDemo(),
		variables: {
			targetEntityId: targetEntityId,
		},
		onError: (e) => {
			// fail silently
			console.log(e);
		},
		onCompleted: async () => {
			await client.refetchQueries({
				updateCache(cache) {
					cache.evict({ fieldName: 'notifications' });
				},
			});
		},
	});
	return {
		unseenCount: data?.notifications.unseenCount,
	};
};

/**
 * The hook to mark a notification as read.
 * @param notificationId
 * @returns
 */
export const useNotificationMarkReadMutation = (notificationId: string) => {
	const toast = useToast();
	const [notificationMarkRead, { error }] = useMutation<Pick<Mutation, 'notificationMarkRead'>>(
		NOTIFICATION_MARK_READ,
		{
			errorPolicy: 'all',
			update: (cache) => {
				cache.modify({
					id: `Notification:${notificationId}`,
					fields: {
						state() {
							return NotificationState.Read;
						},
					},
				});
			},
			onError: () => {
				toast.push(`An error occurred. Please try again later or contact Support.`, {
					variant: 'error',
				});
				console.log(error);
			},
		}
	);

	const readNotification = async () => {
		return notificationMarkRead({
			variables: {
				notificationId,
			},
		});
	};

	return { readNotification };
};

/**
 * The hook to dismiss a notification.
 * @param notificationId
 * @returns
 */
export const useNotificationDismissMutation = (notificationId: string) => {
	const toast = useToast();
	const [notificationDismiss, { error }] = useMutation<Pick<Mutation, 'notificationDismiss'>>(
		NOTIFICATION_DISMISS,
		{
			errorPolicy: 'all',
			update: (cache) => {
				cache.evict({
					id: `Notification:${notificationId}`,
				});
			},
			onError: () => {
				toast.push(`An error occurred. Please try again later or contact Support.`, {
					variant: 'error',
				});
				console.log(error);
			},
		}
	);

	const dismissNotification = async () => {
		return notificationDismiss({
			variables: {
				notificationId,
			},
		});
	};

	return { dismissNotification };
};
